import { defineComponent as _defineComponent } from 'vue'
import { Offer } from '@/@types/graphql-types';
import PromotionRowCTA from '@/features/buybox/components/list/PromotionRowCTA.vue';
import PromotionRowOfferLabel from '@/features/buybox/components/list/PromotionRowOfferLabel.vue';
import PromotionRowOfferPresentation from '@/features/buybox/components/list/PromotionRowOfferPresentation.vue';
import {
	GetPackageProviderPlansDocument,
	GetPackageProviderPlansQuery,
} from '@/features/buybox/graphql/queries/GetPackageProviderPlans.query';
import { useGeoSwiper } from '@/features/buybox/composables/useGeoSwiper';
import { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import ClickoutAnchor from '@/components/nav/links/ClickoutAnchor.vue';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import { useInternationalOffersStatus } from '@/features/international-offers/composables/useInternationalOffers';
import { ClickoutUrl, trackClickout } from '@/helpers/clickout-helper';
import { useQuery } from '@/helpers/composables/useApollo';
import { useArbitrage } from '@/helpers/composables/useArbitrage';
import { observableOptions, useOfferImpressionTracking } from '@/helpers/composables/useOfferImpressionTracking';
import type { UsePromotion } from '@/helpers/composables/usePromotion';
import { usePromotion } from '@/helpers/composables/usePromotion';
import { OFFER_ENTITYID_KEY, getClickoutTrackingPlacement } from '@/helpers/composables/useProvideClickoutPlacement';
import { useAppStore, useLanguageStore } from '@/helpers/composables/useStores';
import { ModalHelper } from '@/helpers/modal-helper';
import { OfferLocation } from '@/helpers/offer-helper';
import { FALLBACK_PROMOTION_OFFER, primeImpactCountries } from '@/helpers/promotion-helper';
import { SnowplowContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import { BuyBoxFilterOptions } from '@/stores/user.store';
import JwTVModal from '@/ui-components/JwTVModal.vue';
import { useYAExperiment } from '@/components/experiments/YoungAdultExp';
import Vue, { computed, inject, ref } from 'vue';
import { useExperiment } from '@/libraries/experiment';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { usePPExperiments } from '@/components/experiments/PPExperiments';
import { useSingleQTV } from '@/features/buybox/composables/useSingleQTV';
import { ThumborOptions } from '@/filters/thumbor';
import PictureComp from '@/components/picture/PictureComp.vue';
import { useI18n } from '@/helpers/composables/useI18n';
import { usePPSwiperExp } from '@/components/experiments/PPExperiments/PPSwiperExp';

export type PlanOffer = ReturnType<typeof getPlanOffer>;


export default /*#__PURE__*/_defineComponent({
  __name: 'ListPromotionRow',
  props: {
    title: null,
    offers: { default: () => [] },
    additionalContexts: { default: () => [] },
    hardcodedFallbackOfferId: null,
    hardcodedCountry: null,
    hardcodedShortName: null,
    hideRemoveAds: { type: Boolean },
    swiperIndex: null
  },
  emits: ["clickOut", "impressionCallback"],
  setup(__props: any, { emit }: { emit: ({
	(
		e: 'clickOut',
		payload: {
			buyboxOffer: UsePromotion['promotionOffer'];
			offerLocation: OfferLocation.FREE_TRIAL;
			row: HTMLElement | null;
		}
	): void;
	(e: 'impressionCallback'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { title: TitleDetail, offers: Offer[], additionalContexts: SnowplowContext[], hardcodedFallbackOfferId?: number, hardcodedCountry?: string | null, hardcodedShortName?: string, hideRemoveAds?: boolean, swiperIndex?: number };





const container = ref<HTMLElement | null>(null);
const titleContext = inject('titleContext', ref<SnowplowTitleContextGraphql | null>(null));
const filterOption = inject('filterOption', ref<BuyBoxFilterOptions | undefined>(undefined));
const titleSourceId = inject<string | null>('titleSourceId', null);
const episodeEntityId = inject<string | undefined>(OFFER_ENTITYID_KEY, undefined);
const noOffer = inject<boolean | undefined>('noOffer', undefined);
const { placement } = getClickoutTrackingPlacement();

const { country, language } = useLanguageStore();
const { xServeTrack } = useAppStore();
const { setNoOfferTag } = useInternationalOffersStatus({ title: () => props.title });

const { setPPExperimentTags } = usePPExperiments('ft');

const { setPPSwiperExpTags } = usePPSwiperExp();

const fetchGeoSwiperOffers = computed(() => !!props.hardcodedCountry && !!props.hardcodedShortName);

const { geoSwiperOffers } = useGeoSwiper({
	title: () => props.title,
	enable: fetchGeoSwiperOffers,
	offerShortName: () => props.hardcodedShortName,
	offerCountry: () => props.hardcodedCountry,
});

const offersForPromotion = computed(() => {
	if (fetchGeoSwiperOffers.value) {
		return geoSwiperOffers.value;
	}

	return props.offers;
});

const countryOrGeoCountry = computed(() => {
	return props.hardcodedCountry ?? country.value;
});

const { inPromotionCatalogue, promotionOffer, catalogueItem } = usePromotion({
	title: () => props.title,
	offers: offersForPromotion,
	hardcodedShortName: () => props.hardcodedShortName,
	countryOrGeoCountry,
});

// YA Exp
const { setYAExpTags } = useYAExperiment({
	title: () => props.title,
	promotionShortName: () => promotionOffer.value?.package.shortName,
});

const packageShortNames = computed(() =>
	fetchGeoSwiperOffers.value ? [props.hardcodedShortName] : [catalogueItem.value?.shortName ?? null].filter(Boolean)
);

const enableGetPackageProviderPlans = computed(
	() =>
		(catalogueItem.value?.placement && !!catalogueItem.value) ||
		(!catalogueItem.value?.placement && !inPromotionCatalogue.value) ||
		!!fetchGeoSwiperOffers.value
);

const { result: packageProviderPlans } = useQuery<GetPackageProviderPlansQuery>(
	GetPackageProviderPlansDocument,
	() => ({
		country: countryOrGeoCountry.value,
		language: language.value,
		packages: packageShortNames.value,
	}),
	{
		enabled: enableGetPackageProviderPlans,
		errorPolicy: 'all',
	}
);

const jwRawEntityId = computed(() => episodeEntityId ?? props.title.id);
const { trackTagEvent, withArbitrageTracking, isArbitrating } = useArbitrage({ entityId: jwRawEntityId });

const crunchyRollUrl = computed(() => {
	const url = props.offers.find(offer => offer.package.packageId === 283)?.standardWebURL;
	if (!url) return 'http://crunchyroll.pxf.io/xk92Nv';

	return url.includes('crunchyroll.pxf.io') ? url : `http://crunchyroll.pxf.io/xk92Nv?u=${url}`;
});

const packagePlan = computed(() => {
	const mainPackage = inPromotionCatalogue.value
		? promotionOffer.value?.package
		: packageProviderPlans.value?.packages?.[0];

	if (!mainPackage) return null;

	return getPlanOffer(mainPackage);
});

const rowNumber = computed(() => {
	if (container.value == null) return undefined;

	const parentBuyBox = container.value.closest?.('.buybox-selector') ?? null;
	const buyBoxRows = Array.from(parentBuyBox?.querySelectorAll('.buybox-selector > *') ?? []);
	const index = buyBoxRows.findIndex(row => row.contains(container.value));

	return index === -1 ? undefined : index;
});

/** Applies `uct_country`, `rowNumber`, `offerLocation`. */
function clickoutBase(u: ClickoutUrl) {
	return u
		.set('uct_country', country.value)
		.addContexts(titleContext.value)
		.setFreeTrial()
		.setTitleSourceId(titleSourceId)
		.updateContext('ClickoutContextGraphql', 'rowNumber', rowNumber.value)
		.updateContext('ClickoutContextGraphql', 'filterOption', filterOption.value)
		.setForTracking('offerLocation', OfferLocation.FREE_TRIAL)
		.updateContext('TitleContextGraphql', 'jwRawEntityId', jwRawEntityId.value)
		.updateContext('ClickoutContextGraphql', 'placement', placement)
		.updateContext('ClickoutContextGraphql', 'clickoutType', 'free_trial')
		.updateContext('ClickoutContextGraphql', 'noOffer', noOffer)
		.updateContext('ClickoutContextGraphql', 'swiperIndex', props.swiperIndex)
		.setPageType(props.title.objectType)
		.setCanary(xServeTrack.value);
}

const hasAppleTvPlusOffer = computed(() => props.offers?.some(offer => offer.package.packageId === 350));
const hasCrunchyRollOffer = computed(() => props.offers?.some(offer => offer.package.packageId === 283));
const hasHuluOffer = computed(() => props.offers?.some(offer => offer.package.packageId === 15));

const clickoutLink = computed(() => {
	if (!promotionOffer.value) return null;

	const packageId = promotionOffer.value?.package.packageId ?? 350;

	// // temporary bypass of uct for Crunchyroll
	if (packageId === 283) {
		return new ClickoutUrl({ ...promotionOffer.value, standardWebURL: crunchyRollUrl.value }).apply(clickoutBase);
	}

	if (isArbitrating.value) {
		const arbitrageUrl = withArbitrageTracking({ offer: promotionOffer, title: () => props.title });
		if (arbitrageUrl) {
			return new ClickoutUrl({ ...promotionOffer.value, standardWebURL: arbitrageUrl.toString() })
				.apply(clickoutBase)
				.skipRedirect();
		}
	}

	const clickoutUrl = new ClickoutUrl(promotionOffer.value).apply(clickoutBase).setFreeTrial();

	if (promotionOffer.value?.package.shortName === 'jyn') {
		const clickoutUrl = new ClickoutUrl({
			...promotionOffer.value,
			standardWebURL: 'https://www.joyn.de/live-tv/prosieben',
		})
			.apply(clickoutBase)
			.set('uct_ft', '1')
			.skipUCT2();
		if (!inPromotionCatalogue.value) {
			clickoutUrl.set('uct_ct', 'fallback');
		}
		return clickoutUrl;
	}

	// GEO_SWIPER
	if (props.hardcodedCountry) {
		if ([9, 119].includes(promotionOffer.value?.package.packageId)) {
			const tag = props.hardcodedCountry === 'GB' ? 'amz-gb-ft-swiper-21' : 'amz-us-ft-swiper-20';

			const clickoutUrl = new ClickoutUrl(promotionOffer.value)
				.apply(clickoutBase)
				.skipYieldOptimization()
				.skipUCT2()
				.setOnOffer('tag', tag)
				.set('uct_ctag', tag)
				.set('uct_country', props.hardcodedCountry);

			if (!inPromotionCatalogue.value) {
				clickoutUrl.set('uct_ct', 'fallback');
			}

			return clickoutUrl.set('r', clickoutUrl.get('r')!.replace('app.primevideo.com', 'www.primevideo.com'));
		}
	}
	// GEO_SWIPER

	if (promotionOffer.value?.package.packageId !== 350 && promotionOffer.value?.id === FALLBACK_PROMOTION_OFFER) {
		if ([9, 119].includes(promotionOffer.value?.package.packageId)) {
			let offer = promotionOffer.value;
			const amazonPrimeUrl = new ClickoutUrl(offer).apply(clickoutBase).set('uct_ct', 'fallback');
			if (!primeImpactCountries.includes(country.value)) {
				amazonPrimeUrl.skipYieldOptimization();
			}

			// temporarily added: add a tag on no offer pages in certain countries, so we can compare them against international offer noOffer pages
			amazonPrimeUrl.apply(setNoOfferTag);

			// YA EXP
			amazonPrimeUrl.apply(setYAExpTags);
			// YA EXP

			// PP_FT_EXP
			amazonPrimeUrl.apply(setPPExperimentTags);
			amazonPrimeUrl.apply(u => setPPSwiperExpTags(u, 'ft'));
			// PP_FT_EXP

			return amazonPrimeUrl.set(
				'r',
				amazonPrimeUrl.get('r')!.replace('app.primevideo.com', 'www.primevideo.com')
			);
		}

		return clickoutUrl;
	}

	const conversionTrackingByPackageId: Record<number, Record<'param' | 'value', string | null>> = {
		350: { value: hasAppleTvPlusOffer.value ? 'atds' : 'natds', param: 'ct' },
		283: { value: hasCrunchyRollOffer.value ? 'free_trial' : null, param: 'subId3' },
		15: { value: hasHuluOffer.value ? 'free_trial' : null, param: 'sub1' },
	};

	const partnerTokenByPackageId: Record<number, Record<'param' | 'value', string>> = {
		350: {
			value: 'justwatch_tv',
			param: 'itsct',
		},
	};

	if (partnerTokenByPackageId[packageId]?.value) {
		clickoutUrl.setOnOffer(partnerTokenByPackageId[packageId].param, partnerTokenByPackageId[packageId].value);
	}

	if (!!conversionTrackingByPackageId[packageId] && conversionTrackingByPackageId[packageId].param) {
		clickoutUrl.setOnOffer(
			conversionTrackingByPackageId[packageId].param!,
			conversionTrackingByPackageId[packageId].value
		);
	}

	if ([9, 119].includes(promotionOffer.value?.package.packageId)) {
		clickoutUrl.set('r', clickoutUrl.get('r')!.replace('app.primevideo.com', 'www.primevideo.com'));

		// PP_FT_EXP
		clickoutUrl.apply(setPPExperimentTags);
		// PP_FT_EXP

		// YA EXP
		clickoutUrl.apply(setYAExpTags);
		// YA EXP
	}

	return clickoutUrl;
});

const { onIntersection } = useOfferImpressionTracking({
	additionalContexts: () => props.additionalContexts,
});

function onVisibilityChanged(isVisible: boolean) {
	if (!isVisible || !clickoutLink.value) return;

	onIntersection(clickoutLink.value, isVisible);
}

const observeVisibilityOptions = computed(() => ({
	...observableOptions,
	callback: (isVisible: boolean) => onVisibilityChanged(isVisible),
}));

const hasPromotedTag = computed(() => !inPromotionCatalogue.value);
// CTA has a border, and different text. Fallback only.
const isHighlighted = computed(
	() => !inPromotionCatalogue.value && (props.title.__typename === 'Show' || props.title.__typename === 'Season')
);
const isModalProvider = computed(() => promotionOffer.value?.package.shortName === 'jwt');

// PP_FT_EXP
const { activeVariantWithControlGroup: ppFreeTrialExpVariant } = useExperiment('PP_FREETRIAL_EXP_2', 'global');

const { activeVariant: ppSwiperVariant } = useExperiment('PP_SWIPER_EXP');

const { tc, t } = useI18n();
const ppFreeTrialDetails = [
	tc('WEBAPP_PP_FT_EXP_DETAILS_1'),
	tc('WEBAPP_PP_FT_EXP_DETAILS_2'),
	tc('WEBAPP_PP_FT_EXP_DETAILS_3'),
];

const isPPFTActiveVariant = computed(() => !!ppFreeTrialExpVariant.value && ppFreeTrialExpVariant.value !== 'control');

const primeBackground = `/${ASSETS_DIR}/img/experiments/pp-exp/prime_bg.jpeg`;

const promotionShortName = computed(() => (['DE', 'US', 'GB'].includes(country.value) ? 'amp' : 'prv'));
const isPPFreeTrialExpVariant3 = computed(() => ppFreeTrialExpVariant.value === 'variant_3');

const { similarTitle } = useSingleQTV(() => props.title, promotionShortName, isPPFreeTrialExpVariant3);

const ppExpBackdrop = computed(() => {
	if (isPPFreeTrialExpVariant3.value)
		return inPromotionCatalogue.value
			? props.title.content.backdrops[0].backdropUrl
			: similarTitle.value?.node.content.backdrops[0]?.backdropUrl || '';
	return primeBackground;
});

const simiarTitleName = computed(() =>
	inPromotionCatalogue.value ? props.title.content.title : similarTitle.value?.node.content.title || ''
);

const simiarTitleText = computed(() => t('WEBAPP_PP_FT_EXP_TITLE_TEXT', { titleName: simiarTitleName.value }));

const isPPExpActiveVariant = computed(() =>
	['variant_1', 'variant_2', 'variant_3'].includes(ppFreeTrialExpVariant.value || '')
);

function imageUrl(url: string | null | undefined, type: 'poster' | 'backdrop' = 'poster') {
	if (!url) return undefined;
	return Vue.filter('Thumbor')({ url, imageType: type } as ThumborOptions)?.replace('{format}', 'jpg');
}

// PP_FT_EXP

function openModal() {
	if (!isModalProvider.value) return;
	ModalHelper.openModal(
		JwTVModal,
		{ offer: promotionOffer.value, title: props.title },
		{ cssClass: 'trailer-modal' },
		'',
		true,
		[]
	);

	if (clickoutLink.value) trackClickout(clickoutLink.value);
	onClickOut();
}

function getPlanOffer(pckg: TitleOfferFragment['package'] | GetPackageProviderPlansQuery['packages'][0]) {
	if (pckg.planOffers.length > 0) {
		const plan = pckg.planOffers.find(offer => offer.isTrial || offer.children.length > 0);
		if (plan && plan.children.length > 0) {
			return { ...plan.children[0], retailPrice: plan.retailPrice };
		}
		return plan;
	}
	return null;
}

function onClickOut() {
	trackTagEvent(promotionOffer);

	emit('clickOut', {
		buyboxOffer: promotionOffer?.value,
		offerLocation: OfferLocation.FREE_TRIAL,
		row: container.value,
	});
}

return { __sfc: true,props, emit, container, titleContext, filterOption, titleSourceId, episodeEntityId, noOffer, placement, country, language, xServeTrack, setNoOfferTag, setPPExperimentTags, setPPSwiperExpTags, fetchGeoSwiperOffers, geoSwiperOffers, offersForPromotion, countryOrGeoCountry, inPromotionCatalogue, promotionOffer, catalogueItem, setYAExpTags, packageShortNames, enableGetPackageProviderPlans, packageProviderPlans, jwRawEntityId, trackTagEvent, withArbitrageTracking, isArbitrating, crunchyRollUrl, packagePlan, rowNumber, clickoutBase, hasAppleTvPlusOffer, hasCrunchyRollOffer, hasHuluOffer, clickoutLink, onIntersection, onVisibilityChanged, observeVisibilityOptions, hasPromotedTag, isHighlighted, isModalProvider, ppFreeTrialExpVariant, ppSwiperVariant, tc, t, ppFreeTrialDetails, isPPFTActiveVariant, primeBackground, promotionShortName, isPPFreeTrialExpVariant3, similarTitle, ppExpBackdrop, simiarTitleName, simiarTitleText, isPPExpActiveVariant, imageUrl, openModal, getPlanOffer, onClickOut, PromotionRowCTA, PromotionRowOfferLabel, PromotionRowOfferPresentation, ClickoutAnchor, ProviderIcon, faCheck, PictureComp }
}

})